import React, { createContext, useContext, useState, useCallback } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions, 
  Button, 
  Snackbar, 
  Alert,
  Box
} from '@mui/material';
import { 
  Warning as WarningIcon,
  Help as HelpIcon,
  Info as InfoIcon
} from '@mui/icons-material';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  severity?: 'warning' | 'danger' | 'info';
}

interface ToastOptions {
  message: string;
  severity?: 'success' | 'error' | 'warning' | 'info';
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context.confirm;
};

export const useToast = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useToast must be used within a ConfirmProvider');
  }
  return context.showToast;
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogOptions, setDialogOptions] = useState<ConfirmOptions>({ message: '' });
  const [resolveRef, setResolveRef] = useState<((value: boolean) => void) | null>(null);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastOptions, setToastOptions] = useState<ToastOptions>({ message: '', severity: 'success' });

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    setDialogOptions({
      title: options.title || 'Confirmation Required',
      message: options.message,
      confirmText: options.confirmText || 'Yes, Continue',
      cancelText: options.cancelText || 'Cancel',
      severity: options.severity || 'warning'
    });
    setDialogOpen(true);
    return new Promise<boolean>((resolve) => {
      setResolveRef(() => resolve);
    });
  }, []);

  const showToast = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setToastOptions({ message, severity });
    setToastOpen(true);
  }, []);

  const handleCancel = () => {
    setDialogOpen(false);
    if (resolveRef) resolveRef(false);
  };

  const handleConfirm = () => {
    setDialogOpen(false);
    if (resolveRef) resolveRef(true);
  };

  const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastOpen(false);
  };

  const getSeverityColor = () => {
    if (dialogOptions.severity === 'danger') return '#d32f2f';
    if (dialogOptions.severity === 'info') return '#0288d1';
    return '#ed6c02'; // warning
  };

  return (
    <ConfirmContext.Provider value={{ confirm, showToast }}>
      {children}

      <Dialog
        open={dialogOpen}
        onClose={handleCancel}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1.5,
            minWidth: { xs: 280, sm: 400 },
            boxShadow: '0 12px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} sx={{ px: 2, pt: 1.5 }}>
          {dialogOptions.severity === 'danger' ? (
            <WarningIcon sx={{ color: '#d32f2f', fontSize: 32 }} />
          ) : dialogOptions.severity === 'info' ? (
            <InfoIcon sx={{ color: '#0288d1', fontSize: 32 }} />
          ) : (
            <HelpIcon sx={{ color: '#ed6c02', fontSize: 32 }} />
          )}
          <DialogTitle 
            id="confirm-dialog-title" 
            sx={{ p: 0, fontWeight: 'bold', fontSize: '1.25rem', color: '#1a237e' }}
          >
            {dialogOptions.title}
          </DialogTitle>
        </Box>
        
        <DialogContent sx={{ mt: 1.5, pb: 1 }}>
          <DialogContentText id="confirm-dialog-description" sx={{ color: '#37474f', fontSize: '0.95rem' }}>
            {dialogOptions.message}
          </DialogContentText>
        </DialogContent>
        
        <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
          <Button 
            onClick={handleCancel} 
            variant="outlined" 
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              color: '#546e7a', 
              borderColor: '#b0bec5',
              '&:hover': {
                borderColor: '#78909c',
                backgroundColor: '#f5f7f8'
              }
            }}
          >
            {dialogOptions.cancelText}
          </Button>
          <Button 
            onClick={handleConfirm} 
            variant="contained" 
            autoFocus
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              backgroundColor: getSeverityColor(),
              '&:hover': {
                backgroundColor: getSeverityColor(),
                filter: 'brightness(0.95)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }
            }}
          >
            {dialogOptions.confirmText}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleToastClose} 
          severity={toastOptions.severity} 
          variant="filled"
          sx={{ 
            width: '100%', 
            borderRadius: 2.5,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            fontWeight: 'bold',
            fontSize: '0.9rem'
          }}
        >
          {toastOptions.message}
        </Alert>
      </Snackbar>
    </ConfirmContext.Provider>
  );
};
